// Central home for "what do we show when a blog image URL is dead" logic.
// source.unsplash.com's old Source API was deprecated years ago and 503s on
// every request now — every AI-generated post made before this fix has its
// cover, og, and inline body images pointing straight at it. picsum.photos
// is the fallback already used for body images below: deterministic (same
// seed -> same photo), no API key, doesn't go down.

const DEAD_IMAGE_HOSTS = ["source.unsplash.com"];

// Pulls the most specific bit out of the dead URL (the query string on an
// Unsplash Source link, or the whole thing otherwise) so the same broken
// URL always maps to the same picsum photo instead of a new random one
// every time it re-renders.
function seedFrom(url: string): string {
  const tail = url.split("?").pop() || url;
  // A url with no query string falls back to the whole thing, slashes and
  // all — picsum's router 404s on an encoded slash (%2F) in the seed
  // segment, so swap them for hyphens before encoding.
  return encodeURIComponent(tail.replace(/\//g, "-")).slice(0, 80);
}

export function picsumFallback(seedSource: string): string {
  return `https://picsum.photos/seed/${seedFrom(seedSource)}/800/450`;
}

export function isDeadImageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return DEAD_IMAGE_HOSTS.some((host) => url.includes(host));
}

/**
 * For the spots a browser can't run an onError handler for us — og:image
 * meta tags, JSON-LD `image` fields, anything a crawler fetches directly
 * without executing our JS. Swaps a known-dead host out immediately and
 * leaves everything else (including a real Supabase Storage URL) untouched.
 */
export function resolveStaticImageUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  return isDeadImageUrl(url) ? picsumFallback(url) : url;
}
